/**
 * ProductionCardModal Component
 *
 * Production card preview shown before confirming substrate batch creation.
 * In the deferred-count flow, the bag count and bag size are not yet known —
 * the card displays one nominal recipe's worth of materials and labels the
 * yield as "expected".
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loader2 } from 'lucide-react'

export default function ProductionCardModal({
  isOpen,
  onClose,
  onAccept,
  recipe,
  blockFormat,
  totalBlocks,
  ingredients = [],
  isLoading = false,
  nominal = false
}) {
  const blockWeight = blockFormat?.weight_kg || 4.5
  const totalWeight = totalBlocks * blockWeight

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Production Card</DialogTitle>
          <DialogDescription>
            {nominal
              ? 'Review the recipe before mixing. Bag count and size are recorded later.'
              : 'Review batch details before creating'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-content-secondary">Recipe</span>
              <span className="text-sm font-medium">{recipe?.name || '-'}</span>
            </div>
            {blockFormat && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-content-secondary">Bag Size</span>
                <Badge variant="outline">{blockFormat.name}</Badge>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-content-secondary">
                {nominal ? 'Expected Blocks' : 'Blocks'}
              </span>
              <span className="text-sm font-medium">
                {nominal ? `~${totalBlocks}` : totalBlocks}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-content-secondary">
                {nominal ? 'Expected Weight' : 'Total Weight'}
              </span>
              <span className="text-sm font-medium">
                {nominal ? `~${totalWeight.toFixed(1)} kg` : `${totalWeight.toFixed(1)} kg`}
              </span>
            </div>
          </div>

          {ingredients.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Ingredients</h4>
              <div className="rounded-md border divide-y">
                {ingredients.map((ing, i) => {
                  const qtyKg = ing.quantity_kg || ing.scaled_quantity || ing.scaled_kg || 0
                  const conv = Number(ing.conversion_factor) || 0
                  const unit = ing.purchase_unit
                  let bagsLabel = null
                  if (unit && conv > 1 && qtyKg > 0) {
                    const bags = qtyKg / conv
                    const whole = Math.abs(bags - Math.round(bags)) < 0.01
                    const bagText = whole ? `${Math.round(bags)}` : `~${bags.toFixed(1)}`
                    bagsLabel = `${bagText} × ${conv}kg ${unit}${Math.round(bags) === 1 ? '' : 's'}`
                  }
                  return (
                    <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span>{ing.name || ing.item_name || ing.material_name || '-'}</span>
                      <span className="text-right text-content-secondary">
                        {bagsLabel ? (
                          <>
                            <span className="font-medium text-content-primary">{bagsLabel}</span>
                            <span className="ml-2 text-xs text-content-tertiary">({qtyKg.toFixed(2)} kg)</span>
                          </>
                        ) : (
                          <>{qtyKg.toFixed(2)} kg</>
                        )}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={onAccept} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Accept & Create'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
